import { safeJsonParse } from '../utils/utils';
import { MNO_ERROR_TITLE } from './index';
import { DOB_ERROR_MSG, DOB_LABELS } from './dateOfBirth';
import { LOOKUP_PAGE_TITLE } from '../constants';
import { ALL_FIELDS_REQUIRED } from './form';

export const TITLE = 'Enter their address';
export const TITLE_SELF = 'Enter your address';
export const PAGE_TYPE = 'demographic';

export const DESCRIPTION = 'We’ll look for this person’s pharmacy records.';
export const DESCRIPTION_SELF = 'We’ll look for your pharmacy records.';

export const SUBMIT_BUTTON_TEXT = 'Search';
export const CANCEL_BUTTON_TEXT = 'Cancel';

export const DEMOGRAPHIC_FORM_LABELS = {
  FIRST_NAME: 'First name',
  LAST_NAME: 'Last name',
  STREET_ADDRESS: 'Street address',
  UNIT: 'Unit, apartment, etc. (optional)',
  CITY: 'City',
  STATE: 'State',
  ZIP_CODE: 'ZIP code',
  DATE_OF_BIRTH: 'Date of birth'
};

export const DEMOGRAPHIC_ERROR_TITLES = {
  DEFAULT: 'We need you to check something'
};

export const DEMOGRAPHIC_VALIDATION = {
  FIRST_NAME_REQUIRED: 'Enter a first name',
  FIRST_NAME_INVALID: 'First name cannot exceed 40 characters',
  LAST_NAME_REQUIRED: 'Enter a last name',
  LAST_NAME_INVALID: 'Last name cannot exceed 40 characters',
  STREET_ADDRESS_REQUIRED: 'Enter a street address',
  STREET_ADDRESS_INVALID: 'Street address cannot exceed 40 characters',
  UNIT_INVALID: 'Unit, apartment, etc. (optional) cannot exceed 40 characters',
  CITY_REQUIRED: 'Enter a city',
  STATE_REQUIRED: 'Select a 2-character abbreviated state',
  STATE_INVALID: 'Select a valid 2-character abbreviated state',
  ZIP_CODE_REQUIRED: 'Enter a 5-digit ZIP code',
  ZIP_CODE_INVALID: 'Enter a valid 5-digit ZIP code',
  DATE_OF_BIRTH_REQUIRED: 'Enter an 8-digit date of birth',
  DATE_OF_BIRTH_INVALID: 'Enter a valid 8-digit date of birth',
  DATE_OF_BIRTH_MINOR: 'Enter a date of birth at least 18 years in the past',
  MEMBER_ID_INVALID: 'Member ID cannot include special characters'
};

export const DEMOGRAPHIC_FORM_HELP_TEXT = {
  CITY: '',
  STATE: '',
  ZIP_CODE: '',
  DATE_OF_BIRTH: 'MMDDYYYY',
  PHONE_NUMBER: 'Enter the number on file with the pharmacy.',
  SECOND_ATTEMPT: {
    STREET_ADDRESS: '',
    UNIT: '',
    CITY: '',
    STATE: '',
    ZIP_CODE: '',
    DATE_OF_BIRTH: 'Patient’s date of birth \n MMDDYYYY'
  }
};

export const errorPageContent = {
  HEADER: 'You must be 18 or older',
  PARAGRAPH: 'You must be 18 or older to use this service.',
  ADDMANAGEPEOPLE: 'Add or manage people',
  PARAGRAPH2: 'If you need help, ',
  PARAGRAPH2CONTINUE: 'you can ',
  mnoH1: 'We need to verify you another way',
  mnoDescription: 'We couldn’t confirm your identity using that phone number.',
  mnoH2: 'Answer 3 questions to prove your identity',
  linkText: 'Answer questions',
  mnowithoutLNH1: `Sorry, we're having some technical problems`,
  mnowithoutLNDescription: `You can go back and try again now, but you might have to come back later. `,
  CUSTOMERCARE: `For help, call us at `,
  CUSTOMERCARENUMBER: `1-888-607-4287`,
  TTY: ' (TTY: ',
  TTYNo: '711)',
  linkTextwithoutLN: 'Go back to try again',
  callCustomerCare: 'tel:18886074287',
  OK: 'Ok'
};

export const errorPageContent_UR = {
  mfaTitle: 'The wrong code was entered too many times',
  mnoTitle: 'We couldn\'t verify that number',
  mfaDescription: 'For your security, you’ll have to wait 30 minutes to try again. If you need help, please call us.',
  TTY: 'TTY: 711',
  restart: 'Restart registration',
  mnoH1: 'We need you to try a different number',
  mnoDescription: (phoneNumber = 'that phone number') => {
    return `We couldn’t verify your identity using ${phoneNumber}.`;
  },
  mnoDescription2: 'Check the number and try again. Or try a different mobile number on the next page.',
  linkText: 'Continue'
};

// Translation configuration for demographic page
export const DEMOGRAPHIC_TRANSLATION_CONFIG = [
  { key: 'TITLE', value: TITLE, message: TITLE },
  { key: 'TITLE_SELF', value: TITLE_SELF, message: TITLE_SELF },
  { key: 'DESCRIPTION', value: DESCRIPTION, message: DESCRIPTION },
  { key: 'DESCRIPTION_SELF', value: DESCRIPTION_SELF, message: DESCRIPTION_SELF },
  { key: 'SUBMIT_BUTTON_TEXT', value: SUBMIT_BUTTON_TEXT, message: SUBMIT_BUTTON_TEXT },
  { key: 'CANCEL_BUTTON_TEXT', value: CANCEL_BUTTON_TEXT, message: CANCEL_BUTTON_TEXT },
  {
    key: 'DEMOGRAPHIC_FORM_LABELS',
    value: DEMOGRAPHIC_FORM_LABELS,
    message: JSON.stringify(DEMOGRAPHIC_FORM_LABELS)
  },
  {
    key: 'DEMOGRAPHIC_ERROR_TITLES',
    value: DEMOGRAPHIC_ERROR_TITLES,
    message: JSON.stringify(DEMOGRAPHIC_ERROR_TITLES)
  },
  {
    key: 'DEMOGRAPHIC_VALIDATION',
    value: DEMOGRAPHIC_VALIDATION,
    message: JSON.stringify(DEMOGRAPHIC_VALIDATION)
  },
  {
    key: 'DEMOGRAPHIC_FORM_HELP_TEXT',
    value: DEMOGRAPHIC_FORM_HELP_TEXT,
    message: JSON.stringify(DEMOGRAPHIC_FORM_HELP_TEXT)
  },
  {
    key: 'errorPageContent',
    value: errorPageContent,
    message: JSON.stringify(errorPageContent)
  },
  {
    key: 'MNO_ERROR_TITLE',
    value: MNO_ERROR_TITLE,
    message: MNO_ERROR_TITLE
  },
  {
    key: 'MODAL',
    value: errorPageContent,
    message: JSON.stringify(errorPageContent)
  },
  { key: 'DOB_ERROR_MSG', value: DOB_ERROR_MSG, message: JSON.stringify(DOB_ERROR_MSG) },
  { key: 'DOB_LABELS', value: DOB_LABELS, message: JSON.stringify(DOB_LABELS) },
  { key: 'LOOKUP_PAGE_TITLE', value: LOOKUP_PAGE_TITLE, message: LOOKUP_PAGE_TITLE },
  { key: 'ALL_FIELDS_REQUIRED', value: ALL_FIELDS_REQUIRED, message: ALL_FIELDS_REQUIRED }
];

// Helper function to process demographic translations
export const processDemographicTranslations = (translatedItems: any) => {
  if (!translatedItems || Object.keys(translatedItems).length === 0) {
    translatedItems = {};
  }

  // Destructure simple string values with fallbacks
  const {
    [TITLE]: TITLE_TRANSLATED = TITLE,
    [TITLE_SELF]: TITLE_SELF_TRANSLATED = TITLE_SELF,
    [DESCRIPTION]: DESCRIPTION_TRANSLATED = DESCRIPTION,
    [DESCRIPTION_SELF]: DESCRIPTION_SELF_TRANSLATED = DESCRIPTION_SELF,
    [SUBMIT_BUTTON_TEXT]: SUBMIT_BUTTON_TEXT_TRANSLATED = SUBMIT_BUTTON_TEXT,
    [CANCEL_BUTTON_TEXT]: CANCEL_BUTTON_TEXT_TRANSLATED = CANCEL_BUTTON_TEXT,
    [LOOKUP_PAGE_TITLE]: LOOKUP_PAGE_TITLE_TRANSLATED = LOOKUP_PAGE_TITLE,
    [ALL_FIELDS_REQUIRED]: ALL_FIELDS_REQUIRED_TRANSLATED = ALL_FIELDS_REQUIRED
  } = translatedItems;

  return {
    TITLE: TITLE_TRANSLATED,
    TITLE_SELF: TITLE_SELF_TRANSLATED,
    PAGE_TYPE: PAGE_TYPE,
    DESCRIPTION: DESCRIPTION_TRANSLATED,
    DESCRIPTION_SELF: DESCRIPTION_SELF_TRANSLATED,
    SUBMIT_BUTTON_TEXT: SUBMIT_BUTTON_TEXT_TRANSLATED,
    CANCEL_BUTTON_TEXT: CANCEL_BUTTON_TEXT_TRANSLATED,
    DEMOGRAPHIC_FORM_LABELS: safeJsonParse(
      translatedItems[JSON.stringify(DEMOGRAPHIC_FORM_LABELS)],
      DEMOGRAPHIC_FORM_LABELS
    ),
    DEMOGRAPHIC_ERROR_TITLES: safeJsonParse(
      translatedItems[JSON.stringify(DEMOGRAPHIC_ERROR_TITLES)],
      DEMOGRAPHIC_ERROR_TITLES
    ),
    DEMOGRAPHIC_VALIDATION: safeJsonParse(
      translatedItems[JSON.stringify(DEMOGRAPHIC_VALIDATION)],
      DEMOGRAPHIC_VALIDATION
    ),
    DEMOGRAPHIC_FORM_HELP_TEXT: safeJsonParse(
      translatedItems[JSON.stringify(DEMOGRAPHIC_FORM_HELP_TEXT)],
      DEMOGRAPHIC_FORM_HELP_TEXT
    ),
    errorPageContent: safeJsonParse(translatedItems[JSON.stringify(errorPageContent)], errorPageContent),
    MNO_ERROR_TITLE: translatedItems[MNO_ERROR_TITLE] || MNO_ERROR_TITLE,
    MODAL: safeJsonParse(translatedItems[JSON.stringify(errorPageContent)], errorPageContent),
    DOB_ERROR_MSG: safeJsonParse(translatedItems[JSON.stringify(DOB_ERROR_MSG)], DOB_ERROR_MSG),
    DOB_LABELS: safeJsonParse(translatedItems[JSON.stringify(DOB_LABELS)], DOB_LABELS),
    LOOKUP_PAGE_TITLE: LOOKUP_PAGE_TITLE_TRANSLATED,
    ALL_FIELDS_REQUIRED: ALL_FIELDS_REQUIRED_TRANSLATED
  };
};
